import { API_URLS, BASE_URL } from './urls';

// ─── Request / Response types ────────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
}

export interface VerifyOtpRequest {
  email: string;
  otp: string;
}

export interface VerifyOtpResponse {
  access_token: string;
  token_type: string;
  role: string;
  name: string;
  teacher_id: number;
}

export interface LogoutResponse {
  message: string;
}

export interface TeacherProfileResponse {
  id: number;
  title: string;
  first_name: string;
  last_name: string;
  full_name: string;
  parent_or_husband_name: string;
  email: string;
  phone: string;
  address: string;
  highest_qualification: string;
  dob: string;
  gender: string;
  aadhar_number: string;
  aadhar_document: string;
  other_document: string;
  teacher_photo: string;
  is_active: boolean;
  expertise: string;
  other_expertise: string;
  created_at: string;
}

export interface SubjectAssignment {
  id: number;
  subject_id: number;
  subject_name: string;
  teacher_id: number | null;
  teacher_name: string | null;
}

export interface TeacherClassMapping {
  id: number;
  class_id: number;
  class_name: string;
  section_id: number;
  section_name: string;
  academic_year: string;
  class_teacher_id: number;
  class_teacher_name: string;
  is_active: boolean;
  subject_assignments: SubjectAssignment[];
  created_at: string;
}

export interface ApiStudent {
  id: number;
  student_uid: string;
  full_name: string;
  roll_number: string;
  status: string;
  gender: string;
  class_grade: { id: number; name: string };
  section: { id: number; name: string };
  academic_year: string;
  father_mobile: string;
  mother_mobile: string;
  photo_url: string;
}

// ─── Token helpers ───────────────────────────────────────────────────────────

const TOKEN_KEY = 'vav_access_token';

export const saveToken  = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const getToken   = ()               => localStorage.getItem(TOKEN_KEY);
export const clearToken = ()               => localStorage.removeItem(TOKEN_KEY);

// ─── Shared fetch wrapper ────────────────────────────────────────────────────

async function apiFetch<T>(
  url: string,
  options: RequestInit,
): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg =
      (data as { detail?: string; message?: string }).detail ??
      (data as { detail?: string; message?: string }).message ??
      `Request failed (${res.status})`;
    throw new Error(msg);
  }

  return data as T;
}

// ─── API functions ───────────────────────────────────────────────────────────

/**
 * Step 1 - send credentials, server replies with an OTP email.
 */
export async function login(body: LoginRequest): Promise<LoginResponse> {
  return apiFetch<LoginResponse>(API_URLS.LOGING, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Step 2 - verify the OTP.
 * Returns token + teacher info on success.
 * Throws if teacher_id is absent (non-teacher account).
 */
export async function verifyOtp(body: VerifyOtpRequest): Promise<VerifyOtpResponse> {
  const data = await apiFetch<VerifyOtpResponse>(API_URLS.VERIFY_OTP, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!data.teacher_id) {
    throw new Error('This account is not linked to a teacher profile.');
  }

  saveToken(data.access_token);
  return data;
}

/**
 * Fetch full teacher profile by ID (called right after verifyOtp).
 */
export async function getTeacher(teacherId: number): Promise<TeacherProfileResponse> {
  const token = getToken();
  return apiFetch<TeacherProfileResponse>(
    `${BASE_URL}api/v1/teachers/get/${teacherId}`,
    {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
}

/**
 * Step 3 - invalidate the session on the server and clear the local token.
 */
export async function logout(): Promise<LogoutResponse> {
  const token = getToken();

  const result = await apiFetch<LogoutResponse>(API_URLS.LOGOUT, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  clearToken();
  return result;
}

/**
 * Fetch all class + section mappings for a given teacher.
 * Filter by class_teacher_id === teacher.id on the caller side
 * to get only the classes this teacher is assigned to.
 */
export async function getTeacherClasses(teacherId: number): Promise<TeacherClassMapping[]> {
  const token = getToken();
  return apiFetch<TeacherClassMapping[]>(API_URLS.GET_CLASSES(teacherId), {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

/**
 * Fetch all students for a given class and section.
 * Returns roll number and name used to display the attendance list.
 */
export async function getStudentsForClassApi(
  classId: number,
  sectionId: number,
): Promise<ApiStudent[]> {
  const token = getToken();
  return apiFetch<ApiStudent[]>(
    API_URLS.GET_STUDENTS_LIST_CLASS_WISE(classId, sectionId),
    {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
}